import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import useFetch from 'core/hooks/api/useFetch';
import usePut from 'core/hooks/api/usePut';
import usePost from 'core/hooks/api/usePost';
import useDelete from 'core/hooks/api/useDelete';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import { DragAndDropContext, DroppableContext, DraggableContext, handleDragEnd } from 'core/services/dragAndDropService';
import EditBannerModal from 'components/admin/common/modal/EditBannerModal';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import { useHandleInputChange, useHandleFileChange, useHandleRemoveImage } from 'core/utils/inputHandlers';
import BannerItem from 'components/admin/common/BannerItem';
import { validateForm } from 'core/utils/validation';
import { Alert } from 'react-bootstrap';

const HomeBannerSlide = () => {
    const { data, refetch } = useFetch(API_ENDPOINTS.HOME_BANNERS);
    const { putData: updateBannerData, loading: updating, error: updateError } = usePut(API_ENDPOINTS.HOME_BANNERS);
    const { postData: createBannerData, loading: creating, error: createError } = usePost(API_ENDPOINTS.HOME_BANNERS);
    const { deleteData: deleteBannerData, loading: deleting, error: deleteError } = useDelete(API_ENDPOINTS.HOME_BANNERS);
    const [items, setItems] = useState([]);
    const [selectedBanner, setSelectedBanner] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [bannerToDelete, setBannerToDelete] = useState(null);
    const [errors, setErrors] = useState({});
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [modalSuccess, setModalSuccess] = useState(false);

    const handleInputChange = useHandleInputChange(setItems);
    const handleFileChange = useHandleFileChange(setItems);
    const handleRemoveImage = useHandleRemoveImage(setItems);

    useEffect(() => {
        if (Array.isArray(data)) {
            setItems(data);
        }
    }, [data]);

    const onDragEnd = useCallback((result) => {
        handleDragEnd(result, items, setItems);
    }, [items]);

    const handleEditClick = useCallback((banner) => {
        setSelectedBanner(banner);
        setShowModal(true);
        setUpdateSuccess(false);
    }, []);

    const handleModalClose = useCallback(() => {
        setShowModal(false);
        setUpdateSuccess(false);
        setTimeout(() => {
            setSelectedBanner(null);
            setErrors({});
        }, 300);
    }, []);

    const validationRules = [
        // { field: 'description', method: 'required' },
        { field: 'link', method: 'required' },
        { field: 'backgroundImage', method: 'imageFormat' },
        { field: 'backgroundImageMobile', method: 'imageFormat' },
    ];

    const handleModalSave = useCallback(async (updatedBanner, useFormData) => {

        const formErrors = validateForm(updatedBanner, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }
        handleInputChange(updatedBanner.index, 'title', updatedBanner.title);
        handleInputChange(updatedBanner.index, 'description', updatedBanner.description);
        handleInputChange(updatedBanner.index, 'link', updatedBanner.link);
        handleInputChange(updatedBanner.index, 'buttonText', updatedBanner.buttonText);
        handleInputChange(updatedBanner.index, 'textPosition', updatedBanner.textPosition);
        handleInputChange(updatedBanner.index, 'backgroundImage', updatedBanner.backgroundImage);
        if (updatedBanner.id) {
            await updateBannerData(updatedBanner.id, updatedBanner, useFormData);
        } else {
            await createBannerData(updatedBanner, useFormData);
        }
        setShowModal(false);
        setSelectedBanner(null);
        setModalSuccess(true);
        refetch();
    }, [handleInputChange, updateBannerData, createBannerData, refetch]);

    const handleUpdateClick = useCallback(async () => {
        setUpdateSuccess(false);
        setModalSuccess(false);
        setErrors({});
        const formErrors = items.map((item, index) => validateForm(item, validationRules));
        const hasErrors = formErrors.some(error => Object.keys(error).length > 0);

        if (hasErrors) {
            setErrors(formErrors);
            setUpdateSuccess(false);
            return;
        }

        const updatePromises = items.map((item, index) => {
            const updatedItem = { ...item, order: index + 1 };
            return updateBannerData(item.id, updatedItem);
        });
        await Promise.all(updatePromises);
        setUpdateSuccess(true);
    }, [items, updateBannerData]);

    const handleCreateClick = useCallback(() => {
        setUpdateSuccess(false);
        setModalSuccess(false);
        setErrors({});
        const newBanner = {
            title: '',
            description: '',
            link: '',
            buttonText: '',
            textPosition: 'center',
            backgroundImage: null,
            order: items.length + 1,
        };
        setSelectedBanner(newBanner);
        setShowModal(true);
    }, [items]);

    const handleDeleteClick = useCallback((banner) => {
        setBannerToDelete(banner);
        setShowDeleteModal(true);
        setUpdateSuccess(false);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (bannerToDelete) {
            await deleteBannerData(bannerToDelete.id);
            refetch();
            setShowDeleteModal(false);
            setUpdateSuccess(false);
            setBannerToDelete(null);
            setModalSuccess(true);
        }
    }, [bannerToDelete, deleteBannerData, refetch]);

    const handleDeleteModalClose = useCallback(() => {
        setShowDeleteModal(false);
        setUpdateSuccess(false);
        setBannerToDelete(null);
        setErrors({});
    }, []);


    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="col p-0 sub-title Service">
                    <h5 className="m-0 text-title">Banner del Home</h5>
                </div>
                <div className="col-8 profile-upload-para text-end">
                    <p className="truncate-text m-0 mb-1" style={{ fontSize: '13px' }}>
                        * Recomendamos un tamaño de 1920px de ancho y 800px de alto. Solo se aceptan imágenes jpg y png.
                    </p>
                </div>
                <hr style={{ border: '2px', margin: '5px' }} />
            </div>
            {updateSuccess && <Alert variant="success">Banners actualizados con éxito.</Alert>}
            {modalSuccess && <Alert variant="success">Operación realizada con éxito.</Alert>}
            {updateError && <Alert variant="danger">Error al actualizar: {updateError.message}</Alert>}
            {createError && <Alert variant="danger">Error al crear: {createError.message}</Alert>}
            {deleteError && <Alert variant="danger">Error al eliminar: {deleteError.message}</Alert>}
            <DragAndDropContext onDragEnd={onDragEnd}>
                <DroppableContext droppableId="droppable">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {items.length > 0 ? (
                                items.map((banner, index) => (
                                    <DraggableContext key={banner.id} draggableId={banner.id.toString()} index={index}>
                                        {(provided) => (
                                            <MemoizedBannerItem
                                                provided={provided}
                                                banner={banner}
                                                index={index}
                                                handleInputChange={handleInputChange}
                                                handleFileChange={handleFileChange}
                                                handleEditClick={handleEditClick}
                                                handleDeleteClick={handleDeleteClick}
                                                errors={errors[index]}
                                            />
                                        )}
                                    </DraggableContext>
                                ))
                            ) : (
                                <p>No hay banners disponibles</p>
                            )}
                            {provided.placeholder}
                        </div>
                    )}
                </DroppableContext>
            </DragAndDropContext>
            <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                <a to="#" className="link-primary add-extra" onClick={handleCreateClick} disabled={updating}>
                  <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                  Agregar Banner
                </a>
                <button className="btn btn-primary" onClick={handleUpdateClick}  disabled={updating}>
                    {updating ? 'Actualizando...' : 'Actualizar Banners'}
                </button>
            </div>
            <EditBannerModal
                show={showModal}
                banner={selectedBanner}
                handleInputChange={handleInputChange}
                handleFileChange={handleFileChange}
                handleRemoveImage={handleRemoveImage}
                handleClose={handleModalClose}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
            />
            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={handleDeleteModalClose}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
            />
        </div>
    );
};

const MemoizedBannerItem = memo(BannerItem);

export default HomeBannerSlide;